<template>
    <Modal2
        design="v3"
        large
        :show-footer="false"
        :show-title="false"
        class-name="ad-detail-modal"
        @cancel="onClose"
    >
        <div v-if="ad" class="ad-detail">
            <!-- Header -->
            <div class="ad-header">
                <div class="ad-header-left">
                    <span
                        class="status-dot"
                        :class="String(ad.effective_status || ad.status || '').toLowerCase()"
                        :title="ad.effective_status || ad.status || ''"
                    ></span>
                    <div class="ad-title-block">
                        <div class="ad-title">{{ ad.name || ad.id }}</div>
                        <div class="ad-id">ID: {{ ad.id }}</div>
                    </div>
                </div>
                <a
                    v-if="metaUrl"
                    class="open-meta"
                    :href="metaUrl"
                    target="_blank"
                    rel="noopener"
                >
                    <i class="material-icons">open_in_new</i>
                    {{ $t("metaInsights.adDetail.openInMeta") }}
                </a>
            </div>

            <!-- Status / dates row -->
            <div class="ad-meta-row">
                <div class="meta-item">
                    <div class="meta-label">{{ $t("metaInsights.adDetail.status") }}</div>
                    <div class="meta-value">
                        <span class="status-badge" :class="String(ad.effective_status || '').toLowerCase()">
                            {{ ad.effective_status || ad.status || "-" }}
                        </span>
                    </div>
                </div>
                <div class="meta-item">
                    <div class="meta-label">{{ $t("metaInsights.adDetail.created") }}</div>
                    <div class="meta-value">{{ formatDate(ad.created_time) }}</div>
                </div>
                <div class="meta-item">
                    <div class="meta-label">{{ $t("metaInsights.adDetail.updated") }}</div>
                    <div class="meta-value">{{ formatDate(ad.updated_time) }}</div>
                </div>
            </div>

            <div class="ad-body">
                <!-- Creative Preview -->
                <div v-if="creative" class="section">
                    <div class="section-title">{{ $t("metaInsights.adDetail.creative") }}</div>
                    <div class="creative-card">
                        <div class="creative-media">
                            <img
                                v-if="creativeImage"
                                :src="creativeImage"
                                :alt="creative.name || ad.name"
                                class="creative-img"
                            />
                            <div v-else class="creative-placeholder">
                                <i class="material-icons">image</i>
                            </div>
                        </div>
                        <div class="creative-body">
                            <div v-if="creativeTitle" class="creative-title">{{ creativeTitle }}</div>
                            <div v-if="creativeMessage" class="creative-message">{{ creativeMessage }}</div>
                            <div v-if="creativeDescription" class="creative-desc">{{ creativeDescription }}</div>
                            <a
                                v-if="creativeLink"
                                class="creative-link"
                                :href="creativeLink"
                                target="_blank"
                                rel="noopener"
                            >
                                <i class="material-icons">link</i>
                                <span>{{ creativeLink }}</span>
                            </a>
                            <div v-if="callToAction" class="creative-cta">
                                <span class="cta-badge">{{ callToAction }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- KPI Grid -->
                <div class="section">
                    <div class="section-title">{{ $t("metaInsights.adDetail.performance") }}</div>
                    <div class="kpi-grid">
                        <div v-for="kpi in kpiList" :key="kpi.key" class="kpi-card">
                            <div class="kpi-label">{{ kpi.label }}</div>
                            <div class="kpi-value">{{ kpi.value }}</div>
                        </div>
                    </div>
                </div>

                <!-- Actions Breakdown -->
                <div v-if="actionRows.length > 0" class="section">
                    <div class="section-title">{{ $t("metaInsights.adDetail.actionBreakdown") }}</div>
                    <div class="action-list">
                        <div v-for="row in actionRows" :key="row.type" class="action-item">
                            <div class="action-info">
                                <div class="action-icon">
                                    <i class="material-icons">{{ row.icon }}</i>
                                </div>
                                <div class="action-label">{{ row.label }}</div>
                            </div>
                            <div class="action-numbers">
                                <div class="action-count">{{ formatNumber(row.value) }}</div>
                                <div v-if="row.cost != null" class="action-cost">
                                    {{ formatCurrency(row.cost) }} / {{ $t("metaInsights.action.perUnit") }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty state if no insights -->
                <div v-if="!hasInsights" class="empty-insights">
                    {{ $t("metaInsights.adDetail.noInsights") }}
                </div>
            </div>
        </div>
    </Modal2>
</template>

<script>
import _ from "lodash";
import Modal2 from "../../../../../components/common/Modal2.vue";

const ACTION_LABELS = {
    link_click: "metaInsights.col.linkClicks",
    lead: "metaInsights.col.leads",
    page_engagement: "metaInsights.col.pageEngagement",
    post_engagement: "metaInsights.col.postEngagement",
    video_view: "metaInsights.col.videoViews",
    post_reaction: "metaInsights.col.reactions",
    comment: "metaInsights.col.comments",
    post: "metaInsights.col.shares",
    "onsite_conversion.messaging_conversation_started_7d": "metaInsights.col.messagingStarted",
    landing_page_view: "metaInsights.action.landingPageView",
    purchase: "metaInsights.action.purchase",
    add_to_cart: "metaInsights.action.addToCart",
    initiate_checkout: "metaInsights.action.initiateCheckout",
    like: "metaInsights.action.like",
    post_interaction_net: "metaInsights.action.postInteractionNet",
    post_interaction_gross: "metaInsights.action.postInteractionGross",
    "onsite_conversion.post_save": "metaInsights.action.postSave",
    "onsite_conversion.post_net_save": "metaInsights.action.postNetSave",
    "onsite_conversion.post_unlike": "metaInsights.action.postUnlike",
    "onsite_conversion.post_net_like": "metaInsights.action.postNetLike",
    "onsite_conversion.lead_grouped": "metaInsights.action.leadGrouped",
    omni_landing_page_view: "metaInsights.action.omniLandingPageView",
    complete_registration: "metaInsights.action.completeRegistration",
    view_content: "metaInsights.action.viewContent",
    search: "metaInsights.action.search",
    offsite_complete_registration_add_meta_leads: "metaInsights.action.offsiteCompleteRegistration",
    offsite_search_add_meta_leads: "metaInsights.action.offsiteSearch",
    offsite_content_view_add_meta_leads: "metaInsights.action.offsiteContentView",
};

// action_type -> material icon
const ACTION_ICONS = {
    link_click: "link",
    lead: "person_add",
    "onsite_conversion.lead_grouped": "person_add",
    page_engagement: "pageview",
    post_engagement: "touch_app",
    post_interaction_net: "insights",
    post_interaction_gross: "insights",
    video_view: "play_circle",
    post_reaction: "favorite",
    like: "thumb_up",
    "onsite_conversion.post_net_like": "thumb_up",
    "onsite_conversion.post_unlike": "thumb_down",
    comment: "chat_bubble",
    post: "share",
    "onsite_conversion.post_save": "bookmark",
    "onsite_conversion.post_net_save": "bookmark",
    "onsite_conversion.messaging_conversation_started_7d": "chat",
    landing_page_view: "web",
    omni_landing_page_view: "web",
    purchase: "shopping_cart",
    add_to_cart: "add_shopping_cart",
    initiate_checkout: "shopping_cart_checkout",
    complete_registration: "how_to_reg",
    view_content: "visibility",
    search: "search",
    offsite_complete_registration_add_meta_leads: "how_to_reg",
    offsite_search_add_meta_leads: "search",
    offsite_content_view_add_meta_leads: "visibility",
};

// snake_case veya dot.case bir action_type'i okunabilir Title Case'e cevirir
const humanizeActionType = (type) => {
    if (!type) return "";
    return String(type)
        .replace(/^onsite_conversion\./, "")
        .replace(/^omni_/, "")
        .replace(/_add_meta_leads$/, "")
        .replace(/[._]/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
};

export default {
    name: "AdDetailModal",
    components: { Modal2 },
    props: {
        ad: { type: Object, default: null },
        currency: { type: String, default: "" },
    },
    computed: {
        insights() {
            return _.get(this.ad, "insights.data[0]") || {};
        },
        hasInsights() {
            return !_.isEmpty(this.insights);
        },
        creative() {
            return _.get(this.ad, "creative") || null;
        },
        objectStorySpec() {
            return _.get(this.creative, "object_story_spec") || {};
        },
        creativeData() {
            const oss = this.objectStorySpec;
            return oss.link_data || oss.video_data || oss.photo_data || {};
        },
        creativeImage() {
            return (
                _.get(this.creative, "image_url") ||
                _.get(this.creativeData, "image_url") ||
                _.get(this.creativeData, "picture") ||
                _.get(this.creative, "thumbnail_url") ||
                ""
            );
        },
        creativeTitle() {
            return (
                _.get(this.creative, "title") ||
                _.get(this.creativeData, "name") ||
                _.get(this.creativeData, "title") ||
                ""
            );
        },
        creativeMessage() {
            return (
                _.get(this.creative, "body") ||
                _.get(this.creativeData, "message") ||
                ""
            );
        },
        creativeDescription() {
            return _.get(this.creativeData, "description") || "";
        },
        creativeLink() {
            return (
                _.get(this.creativeData, "link") ||
                _.get(this.creative, "object_url") ||
                _.get(this.creative, "instagram_permalink_url") ||
                ""
            );
        },
        callToAction() {
            const cta =
                _.get(this.creativeData, "call_to_action.type") ||
                _.get(this.creative, "call_to_action_type") ||
                "";
            return cta ? cta.replace(/_/g, " ") : "";
        },
        metaUrl() {
            const direct = _.get(this.ad, "preview_shareable_link");
            if (direct) return direct;
            const id = _.get(this.ad, "id");
            return id ? "https://www.facebook.com/ads/manager/account/ads/?selected_ad_ids=" + id : "";
        },
        kpiList() {
            const ins = this.insights;
            const t = this.$t.bind(this);
            return [
                { key: "spend", label: t("metaInsights.col.spend"), value: this.formatCurrency(ins.spend) },
                { key: "impressions", label: t("metaInsights.col.impressions"), value: this.formatNumber(ins.impressions) },
                { key: "reach", label: t("metaInsights.col.reach"), value: this.formatNumber(ins.reach) },
                { key: "frequency", label: t("metaInsights.col.frequency"), value: this.formatNumber(ins.frequency) },
                { key: "clicks", label: t("metaInsights.col.clicks"), value: this.formatNumber(ins.clicks) },
                { key: "ctr", label: t("metaInsights.col.ctr"), value: this.formatPercent(ins.ctr) },
                { key: "cpc", label: t("metaInsights.col.cpc"), value: this.formatCurrency(ins.cpc) },
                { key: "cpm", label: t("metaInsights.col.cpm"), value: this.formatCurrency(ins.cpm) },
            ];
        },
        actionRows() {
            const actions = _.get(this.insights, "actions") || [];
            const costs = _.get(this.insights, "cost_per_action_type") || [];
            const costMap = {};
            costs.forEach((c) => {
                if (c && c.action_type) costMap[c.action_type] = Number(c.value);
            });
            return actions
                .map((a) => {
                    const labelKey = ACTION_LABELS[a.action_type];
                    const label = labelKey ? this.$t(labelKey) : humanizeActionType(a.action_type);
                    return {
                        type: a.action_type,
                        label: label,
                        icon: ACTION_ICONS[a.action_type] || "bolt",
                        value: Number(a.value),
                        cost: costMap[a.action_type] != null ? costMap[a.action_type] : null,
                    };
                })
                .sort((a, b) => (b.value || 0) - (a.value || 0));
        },
    },
    methods: {
        onClose() {
            this.$emit("close");
        },
        formatNumber(val) {
            if (val === null || val === undefined || val === "") return "-";
            const num = Number(val);
            if (isNaN(num)) return val;
            return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
        },
        formatPercent(val) {
            if (val === null || val === undefined || val === "") return "-";
            const num = Number(val);
            if (isNaN(num)) return val;
            return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + "%";
        },
        formatCurrency(val) {
            if (val === null || val === undefined || val === "") return "-";
            const num = Number(val);
            if (isNaN(num)) return val;
            return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + " " + (this.currency || "");
        },
        formatDate(val) {
            if (!val) return "-";
            return this.$moment(val).format("DD MMM YYYY HH:mm");
        },
    },
};
</script>

<style lang="scss">
.ant-modal-root.v3.ad-detail-modal {
    .ant-modal {
        width: 720px !important;
    }

    .custom-modal-body {
        padding: 0 !important;
    }

    .ad-detail {
        display: flex;
        flex-direction: column;
        max-height: 80vh;
        overflow: hidden;
    }

    .ad-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding: 24px 28px 16px;
        border-bottom: 1px solid var(--color-border-default);

        .ad-header-left {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            flex: 1;
            min-width: 0;
        }

        .status-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            margin-top: 7px;
            background: #bdbdbd;
            flex: 0 0 auto;

            &.active { background: #52c41a; }
            &.paused { background: #faad14; }
            &.deleted, &.archived { background: #d9d9d9; }
            &.disapproved, &.with_issues { background: #ff4d4f; }
        }

        .ad-title-block {
            min-width: 0;
            flex: 1;
        }

        .ad-title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
            line-height: 1.3;
            word-break: break-word;
        }

        .ad-id {
            font-size: 12px;
            color: var(--color-text-muted-default);
            margin-top: 2px;
        }

        .open-meta {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            color: var(--color-text-info-default);
            font-size: 13px;
            white-space: nowrap;
            flex: 0 0 auto;

            i { font-size: 16px; }
        }
    }

    .ad-meta-row {
        display: flex;
        gap: 24px;
        padding: 14px 28px;
        background: var(--color-background-subtle-default);
        border-bottom: 1px solid var(--color-border-default);

        .meta-item {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        .meta-label {
            font-size: 11px;
            text-transform: uppercase;
            color: var(--color-text-muted-default);
            letter-spacing: 0.4px;
        }

        .meta-value {
            font-size: 13px;
            color: var(--color-text-strong-default);
            font-weight: 500;
        }

        .status-badge {
            padding: 2px 8px;
            border-radius: 10px;
            font-size: 11px;
            text-transform: uppercase;
            background: #f0f0f0;
            color: #595959;

            &.active { background: #f6ffed; color: #389e0d; }
            &.paused { background: #fffbe6; color: #d48806; }
            &.disapproved, &.with_issues { background: #fff1f0; color: #cf1322; }
        }
    }

    .ad-body {
        flex: 1;
        overflow-y: auto;
    }

    .section {
        padding: 18px 28px;
        border-bottom: 1px solid var(--color-border-default);

        &:last-child { border-bottom: none; }

        .section-title {
            font-size: 13px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: var(--color-text-muted-default);
            margin-bottom: 12px;
        }
    }

    .creative-card {
        display: flex;
        gap: 14px;
        align-items: flex-start;

        .creative-media {
            width: 140px;
            flex: 0 0 140px;

            .creative-img {
                width: 140px;
                height: 140px;
                object-fit: cover;
                border-radius: 6px;
                border: 1px solid var(--color-border-default);
            }

            .creative-placeholder {
                width: 140px;
                height: 140px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #fafafa;
                border-radius: 6px;
                border: 1px dashed var(--color-border-default);

                i {
                    font-size: 36px;
                    color: #bfbfbf;
                }
            }
        }

        .creative-body {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 6px;
        }

        .creative-title {
            font-size: 15px;
            font-weight: 600;
            color: var(--color-text-strong-default);
        }

        .creative-message {
            font-size: 13px;
            color: var(--color-text-default);
            white-space: pre-wrap;
            line-height: 1.45;
        }

        .creative-desc {
            font-size: 12px;
            color: var(--color-text-muted-default);
        }

        .creative-link {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;
            color: var(--color-text-info-default);
            word-break: break-all;
            margin-top: 4px;

            i { font-size: 14px; flex: 0 0 auto; }
        }

        .creative-cta {
            margin-top: 6px;
        }

        .cta-badge {
            display: inline-block;
            padding: 3px 10px;
            background: var(--color-background-info-subtle, #e6f7ff);
            color: var(--color-text-info-default, #1890ff);
            border-radius: 10px;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            font-weight: 600;
        }
    }

    .kpi-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 10px;

        .kpi-card {
            background: var(--color-background-subtle-default);
            border: 1px solid var(--color-border-default);
            border-radius: 8px;
            padding: 10px 12px;

            .kpi-label {
                font-size: 11px;
                color: var(--color-text-muted-default);
                text-transform: uppercase;
                letter-spacing: 0.3px;
                margin-bottom: 4px;
            }

            .kpi-value {
                font-size: 16px;
                font-weight: 700;
                color: var(--color-text-strong-default);
            }
        }
    }

    .action-list {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;

        @media (max-width: 720px) {
            grid-template-columns: 1fr;
        }
    }

    .action-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        background: var(--color-background-subtle-default);
        border: 1px solid var(--color-border-default);
        border-radius: 10px;
        transition: border-color 0.15s ease, background 0.15s ease;

        &:hover {
            border-color: var(--color-border-strong-default, #d9d9d9);
            background: #fff;
        }

        .action-info {
            display: flex;
            align-items: center;
            gap: 10px;
            min-width: 0;
            flex: 1;
        }

        .action-icon {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: var(--color-background-info-subtle, #e6f7ff);
            color: var(--color-text-info-default, #1890ff);
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;

            i { font-size: 16px; }
        }

        .action-label {
            font-size: 13px;
            color: var(--color-text-strong-default);
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.3;
            min-width: 0;
        }

        .action-numbers {
            text-align: right;
            flex: 0 0 auto;
        }

        .action-count {
            font-size: 16px;
            font-weight: 700;
            color: var(--color-text-strong-default);
            font-variant-numeric: tabular-nums;
            line-height: 1.2;
        }

        .action-cost {
            font-size: 11px;
            color: var(--color-text-muted-default);
            margin-top: 2px;
            font-variant-numeric: tabular-nums;
        }
    }

    .empty-insights {
        padding: 40px 28px;
        text-align: center;
        color: var(--color-text-muted-default);
        font-size: 13px;
    }
}
</style>
